import { useState, useEffect } from "react";
import {
  Star,
  Plus,
  Edit2,
  Trash2,
  Search,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Save,
} from "lucide-react";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import {
  Card,
  CardContent,
} from "../../../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { Badge } from "../../../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { toast } from "sonner";
import {
  getPropertyGrades,
  createPropertyGrade,
  updatePropertyGrade,
  deletePropertyGrade,
} from "../../../services/propertyGradeService";
import { useScreenPermission } from "../../../hooks/useScreenPermission";
import Pagination from "ui/pagination";
import { Select, SelectContent, SelectItem, SelectValue } from "ui/select";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";

interface PropertyGrade {
  id: string;
  code: string;
  gradeName: string;
  createdAt: string;
  status: "active" | "inactive";
}

export function PropertyGradeMaster() {
  const { setPageHeader } = usePageHeader();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingGrade, setEditingGrade] = useState<PropertyGrade | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [grades, setGrades] = useState<PropertyGrade[]>([]);
  const [loading, setLoading] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState<PropertyGrade | null>(null);

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0); 

  const { canAdd, canEdit, canDelete, screenName, icon: ScreenIcon} = useScreenPermission();

  const [formData, setFormData] = useState({
    code: "",
    gradeName: "",
    status: "active" as "active" | "inactive",
  });

  const [editFormData, setEditFormData] = useState({
    code: "",
    gradeName: "",
    status: "active" as "active" | "inactive",
  });

  const [errors, setErrors] = useState({
    code: "",
    gradeName: "",
  });

  const fetchPropertyGrades = async () => {
    try {
      setLoading(true);
      const res = await getPropertyGrades({
        searchText: searchTerm || null,
        pageNo,
        pageSize,
      });

      const mappedData: PropertyGrade[] = res.items.map((item) => ({
        id: item.publicId,
        code: item.code,
        gradeName: item.name,
        createdAt: item.createdAt,
        status: item.isActive ? "active" : "inactive",
      }));

      setGrades(mappedData);
      setTotalCount(res.totalCount);
    } catch (error) {
      toast.error("Failed to load property grades");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPageHeader({ 
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    fetchPropertyGrades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, pageSize, searchTerm]);

  const handleEdit = (grade: PropertyGrade) => {
    setEditingGrade(grade);
    setEditFormData({
      code: grade.code,
      gradeName: grade.gradeName,
      status: grade.status,
    });
    setIsEditDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingItem) return;

    try {
      const res = await deletePropertyGrade(deletingItem.id);

      if (res === true) {
        toast.success("Property grade deleted successfully");
      }

      setIsDeleteDialogOpen(false);
      setDeletingItem(null);

      fetchPropertyGrades();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message || "Failed to delete property grade";

      toast.error(apiMessage);
    }
  };

  const PROPERTY_GRADE_CODE_REGEX = /^[A-Z0-9]{1,4}$/;

  const PROPERTY_GRADE_NAME_REGEX = /^[A-Za-z0-9\s-]{1,20}$/;

  const handleAddGrade = async () => {
    const newErrors = {
      code: "",
      gradeName: "",
    };

    if (!formData.code.trim()) {
      newErrors.code = "Code is required";
    } else if (!PROPERTY_GRADE_CODE_REGEX.test(formData.code)) {
      newErrors.code =
        "Code cannot exceed 20 characters and contain only letters and numbers.";
    }

    if (!formData.gradeName.trim()) {
      newErrors.gradeName = "Grade Name is required";
    } else if (!PROPERTY_GRADE_NAME_REGEX.test(formData.gradeName.trim())) {
      newErrors.gradeName =
        "Grade Name can contain only letters, numbers, spaces and hyphens (max 20 characters).";
    }

    const codeExists = grades.some(
      (g) => g.code.toLowerCase() === formData.code.toLowerCase(),
    );

    if (codeExists) {
      newErrors.code = "Property Code already used for another property.";
    }

    setErrors(newErrors);

    if (newErrors.code || newErrors.gradeName) return;

    try {
      await createPropertyGrade({
        name: formData.gradeName,
        code: formData.code,
        isActive: formData.status === "active",
      });

      toast.success("Property grade created successfully");

      setIsAddDialogOpen(false);
      resetForm();

      fetchPropertyGrades();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message || "Failed to create property grade";

      toast.error(apiMessage);
    }
  };

  const handleUpdateGrade = async () => {
    if (!editingGrade) return;

    const newErrors = {
      code: "",
      gradeName: "",
    };

    if (!editFormData.code.trim()) {
      newErrors.code = "Code is required";
    } else if (!PROPERTY_GRADE_CODE_REGEX.test(editFormData.code)) {
      newErrors.code =
        "Code cannot exceed 20 characters and contain only letters and numbers.";
    }

    if (!editFormData.gradeName.trim()) {
      newErrors.gradeName = "Grade Name is required";
    } else if (!PROPERTY_GRADE_NAME_REGEX.test(editFormData.gradeName.trim())) {
      newErrors.gradeName =
        "Grade Name can contain only letters, numbers, spaces and hyphens (max 20 characters).";
    }

    const codeExists = grades.some(
      (g) =>
        g.code.toLowerCase() === editFormData.code.toLowerCase() &&
        g.id !== editingGrade.id,
    );

    if (codeExists) {
      newErrors.code = "Property Code already used for another property.";
    }

    setErrors(newErrors);

    if (newErrors.code || newErrors.gradeName) return;

    try {
      const res = await updatePropertyGrade({
        publicId: editingGrade.id,
        name: editFormData.gradeName,
        code: editFormData.code,
        isActive: editFormData.status === "active",
      });

      if (res === true) {
        toast.success("Property grade updated successfully");
      }

      setIsEditDialogOpen(false);
      setEditingGrade(null);

      fetchPropertyGrades();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message || "Failed to update property grade";

      toast.error(apiMessage);
    }
  };

  const resetForm = () => {
    setFormData({
      code: "",
      gradeName: "",
      status: "active",
    });

    setErrors({
      code: "",
      gradeName: "",
    });
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortColumn("");
        setSortDirection("asc");
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Filter and sort
  const filteredGrades = grades
    .filter((grade) =>
      grade.gradeName.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .sort((a, b) => {
      if (!sortColumn) return 0;

      let aVal = a[sortColumn as keyof PropertyGrade];
      let bVal = b[sortColumn as keyof PropertyGrade];

      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6 pb-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-2">
          <div className="relative flex-1 max-w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />

            <Input
              placeholder="Search grades..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPageNo(1);
              }}
              className="h-10 pl-9 pr-9 shadow-sm"
            />

            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setPageNo(1);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                title="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            {canAdd && (
              <Button
                onClick={() => setIsAddDialogOpen(true)}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Grade
              </Button>
            )}
          </div>
        </div>

        {/* Desktop Table */}
        <Card className="bg-white border-[#5B9BD5]/30 hidden lg:block" style={{ marginTop: "10px" }}>
          <CardContent style={{ padding: "0px 0px 10px 0px" }}>
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort("code")}
                  >
                    <div className="flex items-center gap-1">
                      Code
                      {sortColumn === "code" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="w-3 h-3" />
                        ) : (
                          <ArrowDown className="w-3 h-3" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort("gradeName")}
                  >
                    <div className="flex items-center gap-1">
                      Grade Name
                      {sortColumn === "gradeName" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="w-3 h-3" />
                        ) : (
                          <ArrowDown className="w-3 h-3" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort("createdAt")}
                  >
                    <div className="flex items-center gap-1">
                      Created Date
                      {sortColumn === "createdAt" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="w-3 h-3" />
                        ) : (
                          <ArrowDown className="w-3 h-3" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs text-center"
                  >
                    Status
                  </TableHead>
                  {(canEdit || canDelete) && (
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs text-right"
                    >
                      Actions
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredGrades.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center py-8 px-4 text-gray-500 text-sm"
                    >
                      {searchTerm
                        ? "No grades found matching your search"
                        : "No property grades found"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredGrades.map((grade) => (
                    <TableRow key={grade.id} className="hover:bg-sky-50/30">
                      <TableCell className="px-4 py-2">
                        <span className="text-gray-900 text-sm font-mono font-medium">
                          {grade.code}
                        </span>
                      </TableCell>
                      <TableCell className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                            <Star className="w-4 h-4 text-[#5B9BD5]" />
                          </div>
                          <span className="text-gray-900 text-sm font-medium">
                            {grade.gradeName}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-2">
                        <span className="text-gray-700 text-sm">
                          {new Date(grade.createdAt).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            },
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="px-4 py-2 text-center">
                        <Badge
                          variant="outline"
                          className={`${
                            grade.status === "active"
                              ? "bg-green-100 text-green-700 border-green-200"
                              : "bg-gray-100 text-gray-700 border-gray-200"
                          } text-xs`}
                        >
                          {grade.status === "active" ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell
                        className="px-4 py-2 text-right"
                        style={{ gap: "0px" }}
                      >
                        <div className="flex justify-end">
                          {canEdit && (
                            <Button
                              onClick={() => handleEdit(grade)}
                              size="sm"
                              variant="ghost"
                              className="text-[#5B9BD5] hover:text-[#4A8BC2] hover:bg-sky-50 h-8 w-8"
                              style={{ padding: "0px", gap: "0px" }}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              onClick={() => {
                                setDeletingItem(grade);
                                setIsDeleteDialogOpen(true);
                              }}
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8"
                              style={{ padding: "0px", gap: "0px" }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>

          {/* {totalCount > pageSize && ( */}
            <Pagination
              totalItems={totalCount}
              itemsPerPage={pageSize}
              currentPage={pageNo}
              onPageChange={(page) => setPageNo(page)}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPageNo(1);
              }}
              label="grades"
            />
          {/* )} */}
        </Card>

        {/* Mobile Card View */}
        <div className="lg:hidden space-y-4">
          {filteredGrades.length === 0 ? (
            <Card className="border-[#5B9BD5]/30">
              <CardContent className="p-8 text-center">
                <Star className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">
                  {searchTerm
                    ? "No grades found matching your search"
                    : "No property grades found"}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredGrades.map((grade) => (
              <Card key={grade.id} className="border-[#5B9BD5]/30 shadow-sm">
                <CardContent style={{ padding: "10px" }}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                        <Star className="w-5 h-5 text-[#5B9BD5]" />
                      </div>
                      <div>
                        <span className="text-xs font-mono font-medium text-gray-600 bg-gray-100 rounded">
                          {grade.code}
                        </span>
                        <h3 className="text-gray-900 text-sm">
                          {grade.gradeName}
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {new Date(grade.createdAt).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            },
                          )}
                        </p>
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <Badge
                        variant="outline"
                        className={`${
                          grade.status === "active"
                            ? "bg-green-100 text-green-700 border-green-200"
                            : "bg-gray-100 text-gray-700 border-gray-200"
                        } text-xs`}
                      >
                        {grade.status === "active" ? "Active" : "Inactive"}
                      </Badge>
                      <div className="flex justify-end border-t border-gray-100">
                        {canEdit && (
                          <Button
                            onClick={() => handleEdit(grade)}
                            size="sm"
                            variant="ghost"
                            className="text-[#5B9BD5] hover:text-[#4A8BC2] hover:bg-sky-50 h-8 w-8"
                            style={{ padding: "0px", gap: "0px" }}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            onClick={() => {
                              setDeletingItem(grade);
                              setIsDeleteDialogOpen(true);
                            }}
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8"
                            style={{ padding: "0px", gap: "0px" }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div> 
                  </div>
                </CardContent> 
              </Card>
            ))
          )}
          {/* {totalCount > pageSize && ( */}
            <Pagination
              totalItems={totalCount}
              itemsPerPage={pageSize}
              currentPage={pageNo}
              onPageChange={(page) => setPageNo(page)}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPageNo(1);
              }}
              label="grades"
            />
          {/* )} */}
        </div> 

        {/* Add Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="bg-white sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                Add Property Grade
              </DialogTitle>
              <DialogDescription>
                Create a new property grade classification
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <InputFieldset
                  label="Code"
                  required
                  id="code"
                  value={formData.code}
                  onChange={(e) => {
                    const value = e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, "")
                      .slice(0, 4);

                    setFormData({
                      ...formData,
                      code: value,
                    });

                    setErrors({
                      ...errors,
                      code: "",
                    });
                  }}
                  placeholder="Enter code (e.g., 5SD, 4S, B)"
                  className={`text-gray-700 bg-white ${
                    errors.code
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-200"
                  }`}
                />

                {errors.code && (
                  <p className="text-red-500 text-xs">{errors.code}</p>
                )}
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Grade Name"
                  required
                  id="gradeName"
                  value={formData.gradeName}
                  onChange={(e) => {
                    const value = e.target.value
                      .replace(/[^A-Za-z0-9\s-]/g, "")
                      .slice(0, 20);

                    setFormData({
                      ...formData,
                      gradeName: value,
                    });

                    setErrors({
                      ...errors,
                      gradeName: "",
                    });
                  }}
                  placeholder="Enter grade name (e.g., 5-Star, 4-Star, Budget)"
                  className={`bg-white ${
                    errors.gradeName
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-200"
                  }`}
                />

                {errors.gradeName && (
                  <p className="text-red-500 text-xs">{errors.gradeName}</p>
                )}
              </div>
              <div className="space-y-2">
                <SelectFieldset label="Status">
                  <Select
                    value={formData.status}
                    onValueChange={(value: "active" | "inactive") =>
                      setFormData({ ...formData, status: value })
                    }
                  >
                    <SelectTriggerUI className="h-9 text-sm">
                      <SelectValue />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddGrade}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Grade
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-[500px] bg-white">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                Edit Property Grade
              </DialogTitle>
              <DialogDescription>
                Update property grade details
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <InputFieldset
                  label="Code"
                  required
                  id="edit-code"
                  value={editFormData.code}
                  onChange={(e) => {
                    const value = e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, "")
                      .slice(0, 4);

                    setEditFormData({
                      ...editFormData,
                      code: value,
                    });

                    setErrors({
                      ...errors,
                      code: "",
                    });
                  }}
                  className={`bg-white ${
                    errors.code
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-200"
                  }`}
                />

                {errors.code && (
                  <p className="text-red-500 text-xs">{errors.code}</p>
                )}
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Grade Name"
                  required
                  id="edit-gradeName"
                  value={editFormData.gradeName}
                  onChange={(e) => {
                    const value = e.target.value
                      .replace(/[^A-Za-z0-9\s-]/g, "")
                      .slice(0, 20);

                    setEditFormData({
                      ...editFormData,
                      gradeName: value,
                    });

                    setErrors({
                      ...errors,
                      gradeName: "",
                    });
                  }}
                  className={`bg-white ${
                    errors.gradeName
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-200"
                  }`}
                />

                {errors.gradeName && (
                  <p className="text-red-500 text-xs">{errors.gradeName}</p>
                )}
              </div>
              <div className="space-y-2">
                <SelectFieldset label="Status">
                  <Select
                    value={editFormData.status}
                    onValueChange={(value: "active" | "inactive") =>
                      setEditFormData({
                        ...editFormData,
                        status: value,
                      })
                    }
                  >
                    <SelectTriggerUI className="h-9 text-sm">
                      <SelectValue />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setEditingGrade(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateGrade}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                Update Grade
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-red-600">
                Delete Property Grade
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete{" "}
                <span className="font-semibold">
                  "{deletingItem?.gradeName}"
                </span>
                ? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setDeletingItem(null);
                }}
              >
                No, Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Yes, Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
